import React, { useContext, useState } from 'react'
import { Link, useStaticQuery, graphql } from 'gatsby'
import styled from '@emotion/styled'
import tw from 'twin.macro'
import { useSwipeable } from 'react-swipeable'
import { BarLoader } from 'react-spinners'
import { motion, AnimatePresence } from 'framer-motion'
import LineItem from './LineItem'
import { GlobalDispatchContext, GlobalStateContext } from '../../contexts/GlobalContextProvider'
import EmptyCart from './EmptyCart'
import { StoreContext } from '../../contexts/ShopifyStoreContext'
import { formatPrice } from '../../utils/format-price'
import YoutMightLike from './YoutMightLike'
import { isBrowser } from '../../utils/isBrowser'
import useFbq from '../FacebookTracking'

const CartWrap = styled((props) => <motion.div {...props} />)`
  ${tw`fixed right-0 top-0 h-full px-10 lg:px-12 py-24 z-50 overflow-y-scroll text-colekt-dark-gray`}
  background: #F2F2F2;
  -ms-overflow-style: none;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
  max-width: 500px;
  -webkit-overflow-scrolling: touch;
`

const CartContent = styled.div`
  ${tw`relative mb-5`}
`

const CloseButton = styled.button`
  ${tw`text-lg font-extended uppercase tracking-wider`}
  ${tw`p-6 pl-6 focus:outline-none absolute top-0 left-0`}
`

const ContinueShopping = styled.button`
  ${tw`text-base tracking-wide font-extended`}
  ${tw`w-full p-2 text-center hover:underline focus:outline-none`}
`

const Title = styled.h3`
  ${tw`border-b border-black pb-2 text-left font-extended tracking-wide mb-8`}
  font-size: 22px;
  font-weight: 500;
`

const CheckoutButton = styled.button`
  ${tw`flex items-center justify-center h-16 w-full px-5 mb-4 bg-colekt-black text-almost-white uppercase font-extended tracking-widest text-center hover:bg-black`}
  transition: all 0.35s;
  &:hover {
    transition: all 0.35s;
  }
`

const CartTotalsWrap = styled.ul`
  ${tw`font-mono text-base`}
  ${tw`py-4 tracking-wider border-t border-colekt-medium-gray`}
`

const Row = styled.li`
  ${tw`flex mb-1`}

  &:last-of-type {
    ${tw`pt-3 mt-3 border-t border-colekt-medium-gray`}
  }
`

const Label = styled.div`
  ${tw`flex-auto`}
`

const Number = styled.div`
  ${tw`flex-auto text-right`}
`

export default function Cart() {
  const { checkout } = React.useContext(StoreContext)
  const emptyCart = checkout.lineItems.length === 0

  const [checkoutIsLoading, setCheckoutIsLoading] = useState(false)

  const fbq = useFbq()

  const handleCheckout = () => {
    setCheckoutIsLoading(true)

    fbq('InitiateCheckout')

    if (isBrowser) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        event: 'proceed_to_checkout',
      })
    }

    setTimeout(() => {
      const checkoutUrl = checkout.webUrl
      const checkoutUrlCustomDomain = checkoutUrl.replace(
        process.env.GATSBY_SHOPIFY_STORE_URL,
        process.env.GATSBY_SHOPIFY_CUSTOM_DOMAIN
      )
      window.open(checkoutUrlCustomDomain, '_self')
    }, 1100)

    setTimeout(() => {
      setCheckoutIsLoading(false)
    }, 2000)
  }

  const dispatch = useContext(GlobalDispatchContext)
  const state = useContext(GlobalStateContext) || { cartIsOpen: false }

  const { cartIsOpen, cartIsLoading } = state

  const handlers = useSwipeable({
    onSwipedRight: () => dispatch({ type: 'TOGGLE_CART' }),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  })

  const data = useStaticQuery(graphql`
    query {
      shopifyCollection(handle: { eq: "cart" }) {
        ...Collection
      }
      wp {
        globalSettings {
          localizedStrings {
            addToBag
            checkout
            continueShopping
            freeSamplesIncluded
            freeShipping
            helpAndInformation
            hideDetails
            inStockWithFreeShipping
            inclTax
            select
            shipping
            showDetails
            store
            subtotal
            tax
            thanksForSigningUp
            total
            yourBag
            yourBagIsEmpty
          }
        }
      }
    }
  `)

  const {
    checkout: checkoutText,
    total,
    tax,
    yourBag,
    yourBagIsEmpty,
    subtotal,
    shipping,
    freeShipping,
    continueShopping,
    helpAndInformation,
  } = data.wp.globalSettings.localizedStrings

  return (
    <div {...handlers}>
      <AnimatePresence>
        {cartIsOpen && (
          <CartWrap
            className="whiteonblack aside__width"
            initial={{ x: 500 }}
            animate={{ x: 0 }}
            exit={{ x: 500 }}
            transition={{ duration: 0.5, type: 'spring', bounce: 0 }}
            id="cart"
          >
            <CloseButton
              type="button"
              onClick={() => {
                dispatch({ type: 'TOGGLE_CART' })
              }}
            >
              Close
            </CloseButton>

            <Title>{yourBag || 'Your bag'}</Title>

            <CartContent className={`${cartIsLoading ? 'is-loading opacity-25' : 'is-loaded'}`}>
              {emptyCart ? (
                <EmptyCart message={yourBagIsEmpty} />
              ) : (
                <>
                  {checkout.lineItems.map((item) => (
                    <LineItem item={item} key={item.id} />
                  ))}
                  <CartTotalsWrap>
                    <Row>
                      <Label>{subtotal || 'Subtotal'}</Label>
                      <Number>
                        {formatPrice(checkout.subtotalPriceV2.currencyCode, checkout.subtotalPriceV2.amount)}
                      </Number>
                    </Row>
                    <Row>
                      <Label>{shipping || 'Shipping'}</Label>
                      <Number>{freeShipping || 'Free Shipping'}</Number>
                    </Row>
                    <Row>
                      <Label>{tax || 'Tax'}</Label>
                      <Number>{formatPrice(checkout.totalTaxV2.currencyCode, checkout.totalTaxV2.amount)}</Number>
                    </Row>
                    <Row>
                      <Label>{total || 'Total'}</Label>
                      <Number>{formatPrice(checkout.totalPriceV2.currencyCode, checkout.totalPriceV2.amount)}</Number>
                    </Row>
                  </CartTotalsWrap>

                  <CheckoutButton type="button" onClick={handleCheckout} disabled={checkoutIsLoading}>
                    {checkoutIsLoading ? <BarLoader width={150} color="#ffffff" loading /> : checkoutText || 'Checkout'}
                  </CheckoutButton>

                  <ContinueShopping
                    type="button"
                    onClick={() => {
                      dispatch({ type: 'TOGGLE_CART' })
                    }}
                  >
                    {continueShopping || 'Continue shopping'}
                  </ContinueShopping>
                  <Link
                    to="/faq/"
                    onClick={() => {
                      dispatch({ type: 'TOGGLE_CART' })
                    }}
                    className="text-base inline-block tracking-wide font-extended w-full p-2 text-center hover:underline"
                  >
                    {helpAndInformation || 'Help and information'}
                  </Link>
                </>
              )}
            </CartContent>
            {data?.shopifyCollection?.products?.length > 2 && (
              <YoutMightLike title={data?.shopifyCollection.title} products={data?.shopifyCollection.products} />
            )}
          </CartWrap>
        )}
      </AnimatePresence>
    </div>
  )
}
